// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import GameMode from '@app/features/accessibility/state/GameMode';
import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import ActiveIframeEmbed from '@app/features/messaging/state/ActiveIframeEmbed';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import {Avatar} from '@app/features/ui/components/Avatar';
import Tick from '@app/features/ui/state/Tick';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {registerStreamAudioPrefsTouch} from '@app/features/voice/components/StreamAudioPrefsTouchScheduler';
import {getStreamKey} from '@app/features/voice/components/StreamKeys';
import {useStreamWatchState} from '@app/features/voice/components/useStreamWatchState';
import type {VoiceParticipantAvatarEntry} from '@app/features/voice/components/VoiceParticipantAvatarList';
import {useScreenShareAudioPublication} from '@app/features/voice/components/voice_participant_tile/hooks';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {selectVoiceMediaGraphViewerStreamKeys} from '@app/features/voice/engine/VoiceMediaGraph';
import {voiceMediaGraphStore} from '@app/features/voice/engine/VoiceMediaGraphStore';
import {stopWatchingStreamKey} from '@app/features/voice/engine/VoiceStreamWatchState';
import PopoutWindowManager, {getVoiceTilePopoutKey} from '@app/features/voice/state/PopoutWindowManager';
import StreamAudioPrefs from '@app/features/voice/state/StreamAudioPrefs';
import {syncScreenSharePublication} from '@app/features/voice/utils/ScreenShareSubscriptionPolicy';
import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {closeRoomStage} from '@app/features/voice_scene/commands/RoomStageCommands';
import {useCameraMode} from '@app/features/voice_scene/components/useCameraMode';
import {anchorScale, VoiceSceneAnchor} from '@app/features/voice_scene/components/VoiceSceneAnchor';
import styles from '@app/features/voice_scene/components/VoiceSceneOverlay.module.css';
import {VoiceSceneStageBoard} from '@app/features/voice_scene/components/VoiceSceneStageBoard';
import tvStyles from '@app/features/voice_scene/components/VoiceSceneTv.module.css';
import {TvIconButton} from '@app/features/voice_scene/components/VoiceSceneTvIconButton';
import {VoiceSceneTvLinkBox} from '@app/features/voice_scene/components/VoiceSceneTvLinkBox';
import {VoiceSceneTvPicker} from '@app/features/voice_scene/components/VoiceSceneTvPicker';
import {VoiceSceneTvVideo} from '@app/features/voice_scene/components/VoiceSceneTvVideo';
import {
	VoiceSceneTvCaptions,
	VoiceSceneTvModeratorStop,
	VoiceSceneTvVideoControls,
	VoiceSceneTvVolume,
} from '@app/features/voice_scene/components/VoiceSceneTvVideoControls';
import {useRoomWatches} from '@app/features/voice_scene/hooks/useRoomWatches';
import {useTvScreenShareDemand} from '@app/features/voice_scene/hooks/useTvScreenShareDemand';
import type {Vec3, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import VoiceSceneTvRequest from '@app/features/voice_scene/state/VoiceSceneTvRequest';
import {effectiveQualityTier, roomQualityProfile} from '@app/features/voice_scene/utils/RoomQuality';
import {getWebglSupport} from '@app/features/voice_scene/utils/WebglSupport';
import RoomCameraStore from '@app/features/voice_scene/video/RoomCameraStore';
import {defaultTvSource, type TvSource, tvSources} from '@app/features/voice_scene/video/TvSource';
import {MIN_PLAYER_EDGE_PX, youTubeThumbnailUrl} from '@app/features/voice_scene/video/YouTubeTvPlayer';
import Window from '@app/features/window/state/Window';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX} from '@fluxer/constants/src/VoiceRoomStage';
import type {VoiceWatch} from '@fluxer/constants/src/VoiceRoomWatch';
import {getFormattedTime} from '@fluxer/date_utils/src/DateFormatting';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {VoiceRoomStage} from '@fluxer/schema/src/domains/voice_seat/VoiceRoomStageSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {type TrackReference, VideoTrack} from '@livekit/components-react';
import {CornersOutIcon, EyeSlashIcon, XIcon} from '@phosphor-icons/react';
import {type Participant, type RemoteTrackPublication, type Room, Track} from 'livekit-client';
import {computed} from 'mobx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

const WATCH_DESCRIPTOR = msg({
	message: 'Watch',
	comment: "3D voice room TV: button that starts playing someone's screen share or camera on the room's TV.",
});
const CLOSE_DESCRIPTOR = msg({
	message: 'Close',
	comment: '3D voice room TV: button that stops playing the screen share or camera on the TV.',
});
const FULL_SCREEN_DESCRIPTOR = msg({
	message: 'Full screen',
	comment: '3D voice room TV: button that shows what plays on the TV in the full screen call view.',
});
const SHARING_DESCRIPTOR = msg({
	message: '{displayName} is sharing their screen',
	comment: "3D voice room TV: poster on the TV while someone shares their screen. {displayName} is that person's name.",
});
const CAMERA_POSTER_DESCRIPTOR = msg({
	message: "{displayName}'s camera",
	comment: "3D voice room TV: poster on the TV when this viewer picked someone's camera. {displayName} is the name.",
});
const VIDEO_LABEL_DESCRIPTOR = msg({
	message: "{displayName}'s video",
	comment:
		"3D voice room TV: accessible name of the YouTube video someone put on the TV for the room. {displayName} is that person's name.",
});
const STAGE_VIDEO_LABEL_DESCRIPTOR = msg({
	message: "Video on {botName}'s stage",
	comment:
		"3D voice room TV: accessible name of the YouTube video a bot plays on the TV as the room's host. {botName} is the bot's name.",
});
const CLOSE_STAGE_DESCRIPTOR = msg({
	message: 'Close stage',
	comment:
		"3D voice room TV: button shown to a moderator (mute members, move members or manage channel permission) while a bot runs the room's stage; takes the bot's board, video, host character and effects away for everyone in the room.",
});
const HIDE_DESCRIPTOR = msg({
	message: 'Hide',
	comment:
		"3D voice room TV: button that stops showing the shared video on this viewer's TV only; everyone else keeps watching it.",
});

const EMPTY_TITLE_DESCRIPTOR = msg({
	message: 'No one is sharing their screen',
	comment: '3D voice room TV: large line on the switched-off TV screen when nobody shares a screen.',
});
const EMPTY_HINT_DESCRIPTOR = msg({
	message: 'Share your screen from the call controls to show it here.',
	comment:
		'3D voice room TV: small line under "No one is sharing their screen" on the TV, telling how to put a screen on it.',
});

const logger = new Logger('VoiceSceneTv');
const SCREEN_AUDIO_LABEL = 'TV screen share audio publication';

const POSTER_AVATAR_SIZE = 28;
// Past 70° off the TV's normal the poster floats beside a sliver of screen, over the people next to it.
const SIDE_ON_COS = Math.cos((70 * Math.PI) / 180);
const PROBE_STEP_M = 0.25;

type Screen = NonNullable<VoiceSceneHandle['layout']['screen']>;

const px = (value: number): string => `calc(50% + ${Math.round(value * 10) / 10}px)`;

// The poster shows only while the TV faces the camera within 70° and from inside the TV cone, and only
// inside the TV's outline on screen.
function fitPoster(handle: VoiceSceneHandle, screen: Screen, poster: HTMLElement): void {
	const {center, normal, width, height} = screen;
	// The screen's right as seen from the front; screens hang upright, so up is +Y.
	const length = Math.hypot(normal.x, normal.z) || 1;
	const side = {x: normal.z / length, z: -normal.x / length};
	const at = (right: number, up: number, out = 0) =>
		handle.project({
			x: center.x + side.x * right + normal.x * out,
			y: center.y + up,
			z: center.z + side.z * right + normal.z * out,
		});
	const origin = at(0, 0);
	const offset = (right: number, up: number, out = 0) => {
		const point = at(right, up, out);
		return {x: point.x - origin.x, y: point.y - origin.y};
	};
	const right = offset(PROBE_STEP_M, 0);
	const up = offset(0, PROBE_STEP_M);
	const out = offset(0, 0, PROBE_STEP_M);
	// Three orthonormal steps project to squared lengths that add up to twice the squared pixel scale; the signed
	// right × up area over that is the cosine of the view angle, negative from behind.
	const scale2 = (right.x ** 2 + right.y ** 2 + up.x ** 2 + up.y ** 2 + out.x ** 2 + out.y ** 2) / 2;
	const facing = scale2 > 0 ? (up.x * right.y - right.x * up.y) / scale2 : 0;
	const sideOn = facing < SIDE_ON_COS || !handle.screenInView();
	poster.style.display = sideOn ? 'none' : '';
	if (sideOn) return;
	// Corners in the poster's own box: the anchor centres it on the TV and scales it about that centre.
	const scale = anchorScale(origin.scale);
	const corner = (x: number, y: number): string => {
		const point = offset(x, y);
		return `${px(point.x / scale)} ${px(point.y / scale)}`;
	};
	const halfWidth = width / 2;
	const halfHeight = height / 2;
	poster.style.clipPath = `polygon(${corner(-halfWidth, halfHeight)}, ${corner(halfWidth, halfHeight)}, ${corner(halfWidth, -halfHeight)}, ${corner(-halfWidth, -halfHeight)})`;
}

const identityOf = (entry: VoiceParticipantAvatarEntry): string =>
	buildVoiceParticipantIdentity(entry.userId, entry.connectionId);

// Clicks on the TV itself focus the camera on it; its buttons keep their own job.
function focusOnTv(handle: VoiceSceneHandle, target: EventTarget | null): void {
	if (target instanceof Element && target.closest('button')) return;
	handle.focusScreen();
}

type TvProps = {
	handle: VoiceSceneHandle;
	channel: Channel;
	room: Room | null;
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>;
	onScreenPlaying?: (playing: boolean) => void;
	// The room stage a bot runs (useRoomStage); its TV part is a source of its own, first.
	stage?: VoiceRoomStage | null;
};

// A moderator may take a bot's stage away for the room; the server checks again.
export const canCloseRoomStage = (channelId: string): boolean =>
	Permission.can(Permissions.MUTE_MEMBERS, {channelId}) ||
	Permission.can(Permissions.MOVE_MEMBERS, {channelId}) ||
	Permission.can(Permissions.MANAGE_CHANNELS, {channelId});

export function VoiceSceneStageClose({channelId}: {channelId: string}) {
	const {i18n} = useLingui();
	return (
		<Button
			small
			compact
			variant="inverted"
			onClick={() => void closeRoomStage(i18n, channelId)}
			data-flx="voice-scene.voice-scene-tv.voice-scene-stage-close.button"
		>
			{i18n._(CLOSE_STAGE_DESCRIPTOR)}
		</Button>
	);
}

// The TV plays what this viewer picks: by default the first screen share, as before; a camera only when
// picked. Nothing plays until Watch — a screen share through the call's own watch flow, a camera as the room plan's one
// high-quality subscription — so being in the room pulls no extra traffic from the voice server.
export const VoiceSceneTv = observer(function VoiceSceneTv({
	handle,
	channel,
	room,
	entries,
	onScreenPlaying,
	stage = null,
}: TvProps) {
	const {i18n} = useLingui();
	const screen = handle.layout.screen;
	const assignment = readVoiceRoomAssignment(ExperimentAssignments.response);
	const camerasOn = assignment.cameras && VoiceSceneSettings.showRoomCameras;
	// room_watch_enabled, as the assignment's `watch`; videos from before it last came on are stale (watch_since).
	const watchOn = assignment.watch;
	const watches = useRoomWatches(watchOn ? room : null, assignment.watch_since ?? null);
	// A server-muted person's video does not show.
	const links = new Set(
		entries
			.filter(
				(item) => watches.has(identityOf(item)) && !MediaEngine.getVoiceStateByConnectionId(item.connectionId)?.mute,
			)
			.map(identityOf),
	);
	const stageSource = stage?.tv
		? {identity: VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX + stage.bot.id, name: stage.bot.name}
		: null;
	// The stage's video as the room TV's shared time: a new value only when the bot writes again.
	const stageTv = stage?.tv ?? null;
	const stageWrittenAt = stage?.written_at ?? 0;
	const stageWatch = useMemo<VoiceWatch | null>(
		() =>
			stageTv?.type === 'video'
				? {
						videoId: stageTv.video_id,
						startedAtMs: stageTv.started_at,
						pausedAtMs: stageTv.paused_at,
						writtenAtMs: stageWrittenAt,
					}
				: null,
		[stageTv, stageWrittenAt],
	);
	const sources = tvSources(entries, Authentication.currentUserId, links, stageSource).filter(
		(source) => source.kind !== 'camera' || camerasOn,
	);
	const linkBox =
		watchOn && (!channel.guildId || Permission.can(Permissions.STREAM, {channelId: channel.id})) ? (
			<VoiceSceneTvLinkBox channel={channel} data-flx="voice-scene.voice-scene-tv.voice-scene-tv-link-box" />
		) : null;
	const selected = VoiceSceneTvRequest.selected(channel.id);
	const current =
		sources.find((source) => source.kind === selected?.kind && source.identity === selected.identity) ??
		defaultTvSource(sources);
	// the TV moving off a screen share that is still shared stops watching it through the call's own watch
	// path, as the camera's watch ends on a switch; it plays nowhere else in the room. The share ending is the call's
	// to handle, and the room view closing (full screen, the flat view) keeps the watch. So does a popout that plays it
	// — the channel's call popout or that share's own tile popout — as the room's cameras leave popouts alone.
	const shownScreen = current?.kind === 'screen' ? current.identity : null;
	const lastScreen = useRef(shownScreen);
	useEffect(() => {
		const left = lastScreen.current;
		lastScreen.current = shownScreen;
		if (left === null || left === shownScreen) return;
		const sharer = entries.find((item) => item.hasScreenShare && identityOf(item) === left);
		if (!sharer) return;
		if (
			PopoutWindowManager.isCallPopoutOpenForChannel(channel.id) ||
			PopoutWindowManager.isOpen(getVoiceTilePopoutKey(left, 'screen_share'))
		) {
			return;
		}
		const guildId = channel.guildId ?? null;
		stopWatchingStreamKey(getStreamKey(guildId, channel.id, sharer.connectionId), {guildId, channelId: channel.id});
	}, [shownScreen, entries, channel]);
	if (!screen) return null;
	const picker =
		sources.length > 1 || !current ? (
			<VoiceSceneTvPicker
				channel={channel}
				entries={entries}
				sources={sources}
				current={current}
				data-flx="voice-scene.voice-scene-tv.voice-scene-tv-picker"
			/>
		) : null;
	if (current?.kind === 'stage' && stage?.tv) {
		const close = canCloseRoomStage(channel.id) ? (
			<VoiceSceneStageClose channelId={channel.id} data-flx="voice-scene.voice-scene-tv.voice-scene-stage-close" />
		) : null;
		if (stage.tv.type === 'board')
			return (
				<StageBoardTv
					handle={handle}
					screen={screen}
					guildId={channel.guildId ?? null}
					channelId={channel.id}
					entries={entries}
					board={stage.tv}
					bot={stage.bot}
					data-flx="voice-scene.voice-scene-tv.stage-board-tv"
				>
					{picker}
					{linkBox}
					{close}
				</StageBoardTv>
			);
		if (!stageWatch) return null;
		return (
			<LinkTv
				key={`stage:${current.identity}`}
				handle={handle}
				screen={screen}
				watch={stageWatch}
				label={i18n._(STAGE_VIDEO_LABEL_DESCRIPTOR, {botName: stage.bot.name})}
				opener={false}
				onScreenPlaying={onScreenPlaying}
				data-flx="voice-scene.voice-scene-tv.link-tv--2"
			>
				{picker}
				{linkBox}
				{close}
			</LinkTv>
		);
	}
	const entry = current ? entries.find((item) => identityOf(item) === current.identity) : undefined;
	if (!current || !entry) {
		return (
			<>
				<EmptyTv handle={handle} channel={channel} data-flx="voice-scene.voice-scene-tv.empty-tv" />
				{sources.length > 0 || linkBox ? (
					<TvBar handle={handle} screen={screen} data-flx="voice-scene.voice-scene-tv.tv-bar">
						{sources.length > 0 ? picker : null}
						{linkBox}
					</TvBar>
				) : null}
			</>
		);
	}
	const props = {handle, channel, room, entry, screen, picker, linkBox, onScreenPlaying};
	if (current.kind === 'link') {
		const watch = watches.get(current.identity);
		if (!watch) return null;
		const opener = entry.isLocal;
		const moderator =
			!opener &&
			(Permission.can(Permissions.MUTE_MEMBERS, {channelId: channel.id}) ||
				Permission.can(Permissions.MOVE_MEMBERS, {channelId: channel.id}));
		return (
			<LinkTv
				key={`link:${current.identity}`}
				handle={handle}
				screen={screen}
				watch={watch}
				label={i18n._(VIDEO_LABEL_DESCRIPTOR, {
					displayName: NicknameUtils.getNickname(entry.user, channel.guildId ?? null, channel.id),
				})}
				opener={opener}
				onScreenPlaying={onScreenPlaying}
				data-flx="voice-scene.voice-scene-tv.link-tv"
			>
				{picker}
				{linkBox}
				{opener ? (
					<VoiceSceneTvVideoControls
						channelId={channel.id}
						watch={watch}
						data-flx="voice-scene.voice-scene-tv.link-tv.voice-scene-tv-video-controls"
					/>
				) : null}
				{moderator ? (
					<VoiceSceneTvModeratorStop
						channelId={channel.id}
						userId={entry.userId}
						data-flx="voice-scene.voice-scene-tv.link-tv.voice-scene-tv-moderator-stop"
					/>
				) : null}
			</LinkTv>
		);
	}
	return current.kind === 'screen' ? (
		<ScreenTv key={`screen:${current.identity}`} {...props} data-flx="voice-scene.voice-scene-tv.screen-tv" />
	) : (
		<CameraTv key={`camera:${current.identity}`} {...props} data-flx="voice-scene.voice-scene-tv.camera-tv" />
	);
});

type SourceTvProps = {
	handle: VoiceSceneHandle;
	channel: Channel;
	room: Room | null;
	entry: VoiceParticipantAvatarEntry;
	screen: Screen;
	picker: React.ReactNode;
	linkBox: React.ReactNode;
	onScreenPlaying?: (playing: boolean) => void;
};

const ScreenTv = observer(function ScreenTv(props: SourceTvProps) {
	const {i18n} = useLingui();
	const {channel, room, entry} = props;
	const guildId = channel.guildId ?? null;
	const streamKey = getStreamKey(guildId, channel.id, entry.connectionId);
	const {isWatching, canWatch, startWatching, stopWatching} = useStreamWatchState({
		streamKey,
		guildId,
		channelId: channel.id,
	});
	const identity = identityOf(entry);
	const participant = room?.getParticipantByIdentity(identity);
	const publication = participant?.getTrackPublication(Track.Source.ScreenShare);
	const displayName = NicknameUtils.getNickname(entry.user, guildId, channel.id);
	return (
		<>
			{isWatching && participant && !participant.isLocal ? (
				<ScreenTvAudio
					participant={participant}
					userId={entry.userId}
					streamKey={streamKey}
					data-flx="voice-scene.voice-scene-tv.screen-tv.screen-tv-audio"
				/>
			) : null}
			<Tv
				{...props}
				source={{kind: 'screen', identity}}
				trackRef={
					isWatching && participant && publication ? {participant, publication, source: Track.Source.ScreenShare} : null
				}
				posterLabel={i18n._(SHARING_DESCRIPTOR, {displayName})}
				canWatch={canWatch}
				onWatch={startWatching}
				onStop={stopWatching}
				data-flx="voice-scene.voice-scene-tv.screen-tv.tv"
			/>
		</>
	);
});

// The stream's audio, as the call tile plays it: subscribed while watched, silent while muted in its audio settings.
// The TV leaving keeps it while the watch goes on elsewhere (the call tile, a popout).
const ScreenTvAudio = observer(function ScreenTvAudio({
	participant,
	userId,
	streamKey,
}: {
	participant: Participant;
	userId: string;
	streamKey: string;
}) {
	const {publication} = useScreenShareAudioPublication(participant, true);
	const muted = StreamAudioPrefs.isMuted(streamKey);
	const volume = StreamAudioPrefs.getVolume(streamKey);
	const hasPrefsEntry = StreamAudioPrefs.hasEntry(streamKey);
	useEffect(() => {
		if (!publication) return;
		syncScreenSharePublication({
			publication,
			label: SCREEN_AUDIO_LABEL,
			shouldSubscribe: true,
			shouldEnable: !muted,
			onError: (operation, label, err) => logger.error(`${operation} failed for ${label}`, err),
		});
		MediaEngine.applyLocalAudioPreferencesForUser(userId);
	}, [publication, muted, volume, userId]);
	useEffect(() => {
		if (!publication || !hasPrefsEntry) return;
		return registerStreamAudioPrefsTouch(streamKey);
	}, [publication, hasPrefsEntry, streamKey]);
	useEffect(() => {
		if (!publication) return;
		return () => {
			// After this commit's effects: switching the TV away ends the watch in VoiceSceneTv's effect, which runs later.
			queueMicrotask(() => {
				if (selectVoiceMediaGraphViewerStreamKeys(voiceMediaGraphStore.getGraphSnapshot()).includes(streamKey)) return;
				syncScreenSharePublication({
					publication,
					label: SCREEN_AUDIO_LABEL,
					shouldSubscribe: false,
					onError: (operation, label, err) => logger.error(`${operation} failed for ${label}`, err),
				});
			});
		};
	}, [publication, streamKey]);
	return null;
});

const CameraTv = observer(function CameraTv(props: SourceTvProps) {
	const {i18n} = useLingui();
	const {channel, room, entry} = props;
	const identity = identityOf(entry);
	// Plays once watched and planned; the plan drops it when the camera is locally disabled.
	const watching = VoiceSceneTvRequest.watchedCamera(channel.id) === identity;
	const participant = room?.getParticipantByIdentity(identity);
	const publication = participant?.getTrackPublication(Track.Source.Camera);
	const playing = watching && RoomCameraStore.qualityOf(identity) !== null;
	const displayName = NicknameUtils.getNickname(entry.user, channel.guildId ?? null, channel.id);
	return (
		<Tv
			{...props}
			source={{kind: 'camera', identity}}
			trackRef={playing && participant && publication ? {participant, publication, source: Track.Source.Camera} : null}
			posterLabel={i18n._(CAMERA_POSTER_DESCRIPTOR, {displayName})}
			canWatch={!watching}
			onWatch={() => VoiceSceneTvRequest.watchCamera(channel.id, identity)}
			onStop={VoiceSceneTvRequest.stopCamera}
			data-flx="voice-scene.voice-scene-tv.camera-tv.tv"
		/>
	);
});

// The time on the switched-off TV, in the app language's clock: one shared ticker (Tick), redrawn once a minute.
const TvClock = observer(function TvClock() {
	const {i18n} = useLingui();
	const [minute] = useState(() => computed(() => Math.floor(Tick.nowSecond / 60)));
	const at = minute.get() * 60_000;
	return (
		<time
			className={tvStyles.emptyClock}
			dateTime={new Date(at).toISOString()}
			data-flx="voice-scene.voice-scene-tv.tv-clock"
		>
			{getFormattedTime(at, i18n.locale)}
		</time>
	);
});

// The standby glow drifts only on a tier with ambient motion, while the window has focus, the TV is in view and neither
// reduced motion nor lite mode is on, stepped at the room's ambient rate like its own ambient frames; otherwise it stands
// still. The drift is the CSS animation, paused; each step sets its time (an animation that runs on its own is drawn
// every display refresh). Back in view it starts over, as a fresh animation would.
const TvLight = observer(function TvLight({handle}: {handle: VoiceSceneHandle}) {
	const ref = useRef<HTMLDivElement>(null);
	const inView = useScreenInView(handle);
	const stepsPerSecond =
		inView && Window.isFocused() && !Accessibility.useReducedMotion && !GameMode.lightMode
			? roomQualityProfile(effectiveQualityTier(VoiceSceneSettings, getWebglSupport())).ambientMotionHz
			: 0;
	useEffect(() => {
		const light = ref.current;
		if (!light || stepsPerSecond <= 0) return;
		const start = performance.now();
		const timer = window.setInterval(() => {
			for (const drift of light.getAnimations()) drift.currentTime = performance.now() - start;
		}, 1000 / stepsPerSecond);
		return () => window.clearInterval(timer);
	}, [stepsPerSecond]);
	return (
		<div
			ref={ref}
			className={tvStyles.emptyLight}
			aria-hidden="true"
			data-ambient={stepsPerSecond > 0 ? '' : undefined}
			data-flx="voice-scene.voice-scene-tv.tv-light.light"
		/>
	);
});

// With nothing on, the screen itself (on its quad, in the DOM layer like a picture) is a standby screen: the time, how
// to put something on, the channel. Plain text only; its slow light is a CSS transform stepped at the room's ambient
// rate and stops while the TV is out of view (the layer hides it).
function EmptyTv({handle, channel}: {handle: VoiceSceneHandle; channel: Channel}) {
	const {i18n} = useLingui();
	const [screenElement] = useState(() => {
		const element = document.createElement('div');
		element.className = styles.screen;
		return element;
	});
	useEffect(() => {
		const focus = (event: Event) => focusOnTv(handle, event.target);
		screenElement.addEventListener('click', focus);
		screenElement.addEventListener('dblclick', focus);
		handle.attachScreenElement(screenElement);
		return () => {
			screenElement.removeEventListener('click', focus);
			screenElement.removeEventListener('dblclick', focus);
			handle.attachScreenElement(null);
		};
	}, [handle, screenElement]);
	return createPortal(
		<div className={tvStyles.empty} data-tv-empty="" data-flx="voice-scene.voice-scene-tv.empty-tv.empty">
			<TvLight handle={handle} data-flx="voice-scene.voice-scene-tv.empty-tv.tv-light" />
			<TvClock data-flx="voice-scene.voice-scene-tv.empty-tv.tv-clock" />
			<div data-flx="voice-scene.voice-scene-tv.empty-tv.message">
				<p className={tvStyles.emptyTitle} data-flx="voice-scene.voice-scene-tv.empty-tv.title">
					{i18n._(EMPTY_TITLE_DESCRIPTOR)}
				</p>
				<p className={tvStyles.emptyHint} data-flx="voice-scene.voice-scene-tv.empty-tv.hint">
					{i18n._(EMPTY_HINT_DESCRIPTOR)}
				</p>
			</div>
			<span className={tvStyles.emptyChannel} data-flx="voice-scene.voice-scene-tv.empty-tv.channel">
				{channel.name}
			</span>
		</div>,
		screenElement,
	);
}

// A bot's board on the screen itself (on its quad, like EmptyTv's standby screen), its buttons pressable there; the bar
// holds the source picker and a moderator's close.
function StageBoardTv({
	handle,
	screen,
	guildId,
	channelId,
	entries,
	board,
	bot,
	children,
}: {
	handle: VoiceSceneHandle;
	screen: Screen;
	guildId: string | null;
	channelId: string;
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>;
	board: React.ComponentProps<typeof VoiceSceneStageBoard>['board'];
	bot: VoiceRoomStage['bot'];
	children: React.ReactNode;
}) {
	const [screenElement] = useState(() => {
		const element = document.createElement('div');
		element.className = styles.screen;
		return element;
	});
	useEffect(() => {
		const focus = (event: Event) => focusOnTv(handle, event.target);
		screenElement.addEventListener('click', focus);
		screenElement.addEventListener('dblclick', focus);
		handle.attachScreenElement(screenElement);
		return () => {
			screenElement.removeEventListener('click', focus);
			screenElement.removeEventListener('dblclick', focus);
			handle.attachScreenElement(null);
		};
	}, [handle, screenElement]);
	return (
		<>
			{createPortal(
				<VoiceSceneStageBoard
					board={board}
					bot={bot}
					guildId={guildId}
					channelId={channelId}
					entries={entries}
					data-flx="voice-scene.voice-scene-tv.stage-board-tv.voice-scene-stage-board"
				/>,
				screenElement,
			)}
			<TvBar handle={handle} screen={screen} data-flx="voice-scene.voice-scene-tv.stage-board-tv.tv-bar">
				{children}
			</TvBar>
		</>
	);
}

// While a picture plays on the TV the room knows it (a cinema lowers its house lights, runs its projector and
// quiets its ambience).
function useScreenPlaying(
	handle: VoiceSceneHandle,
	playing: boolean,
	onScreenPlaying: ((playing: boolean) => void) | undefined,
): void {
	useEffect(() => {
		if (!playing) return;
		handle.setScreenPlaying?.(true);
		onScreenPlaying?.(true);
		return () => {
			handle.setScreenPlaying?.(false);
			onScreenPlaying?.(false);
		};
	}, [handle, playing, onScreenPlaying]);
}

// the TV's buttons, like its picture, show only from inside the TV cone (the orbit goes all round).
function useScreenInView(handle: VoiceSceneHandle): boolean {
	const [inView, setInView] = useState(() => handle.screenInView());
	useEffect(() => {
		setInView(handle.screenInView());
		return handle.onViewChange(() => setInView(handle.screenInView()));
	}, [handle]);
	return inView;
}

// The TV's bottom-left corner (as seen from the front; screens hang upright; the room toolbar takes the right edge of
// the view, over the TV's right corner in TV focus), at screen size. While a picture
// plays (`playing`) the group is faint until the pointer is over the TV picture (`screenElement`) or over the group.
function TvBar({
	handle,
	screen,
	playing = false,
	screenElement = null,
	children,
}: {
	handle: VoiceSceneHandle;
	screen: Screen;
	playing?: boolean;
	screenElement?: HTMLElement | null;
	children: React.ReactNode;
}) {
	const inView = useScreenInView(handle);
	const [hot, setHot] = useState(false);
	useEffect(() => {
		if (!screenElement) return;
		const enter = () => setHot(true);
		const leave = () => setHot(false);
		screenElement.addEventListener('pointerenter', enter);
		screenElement.addEventListener('pointerleave', leave);
		return () => {
			screenElement.removeEventListener('pointerenter', enter);
			screenElement.removeEventListener('pointerleave', leave);
			setHot(false);
		};
	}, [screenElement]);
	const corner = useMemo<Vec3>(() => {
		const {center, normal, width, height} = screen;
		const length = Math.hypot(normal.x, normal.z) || 1;
		return {
			x: center.x - ((normal.z / length) * width) / 2,
			y: center.y - height / 2,
			z: center.z + ((normal.x / length) * width) / 2,
		};
	}, [screen]);
	return (
		<VoiceSceneAnchor
			point={corner}
			controls
			fixedScale
			data-flx="voice-scene.voice-scene-tv.tv-bar.voice-scene-anchor"
		>
			<div
				className={tvStyles.bar}
				style={inView ? undefined : {display: 'none'}}
				data-playing={playing ? '' : undefined}
				data-hot={hot ? '' : undefined}
				data-flx="voice-scene.voice-scene-tv.tv-bar.bar"
			>
				{children}
			</div>
		</VoiceSceneAnchor>
	);
}

const Tv = observer(function Tv({
	handle,
	channel,
	entry,
	screen,
	picker,
	linkBox,
	source,
	trackRef,
	posterLabel,
	canWatch,
	onWatch,
	onStop,
	onScreenPlaying,
}: SourceTvProps & {
	source: TvSource;
	trackRef: TrackReference | null;
	posterLabel: string;
	canWatch: boolean;
	onWatch: () => void;
	onStop: () => void;
}) {
	const {i18n} = useLingui();
	const [screenElement] = useState(() => {
		const element = document.createElement('div');
		element.className = styles.screen;
		return element;
	});
	const playing = trackRef !== null;
	useScreenPlaying(handle, playing, onScreenPlaying);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	useTvScreenShareDemand({
		handle,
		publication: trackRef?.publication as RemoteTrackPublication | undefined,
		videoRef,
		enabled: playing && source.kind === 'screen' && !trackRef?.participant.isLocal,
	});
	useEffect(() => {
		if (!playing) return;
		const focus = (event: Event) => focusOnTv(handle, event.target);
		screenElement.addEventListener('click', focus);
		screenElement.addEventListener('dblclick', focus);
		handle.attachScreenElement(screenElement);
		return () => {
			screenElement.removeEventListener('click', focus);
			screenElement.removeEventListener('dblclick', focus);
			handle.attachScreenElement(null);
		};
	}, [handle, playing, screenElement]);
	const posterRef = useRef<HTMLDivElement | null>(null);
	useLayoutEffect(() => {
		const poster = posterRef.current;
		if (playing || !poster) return;
		fitPoster(handle, screen, poster);
		return handle.onViewChange(() => fitPoster(handle, screen, poster));
	}, [handle, playing, screen]);
	if (trackRef) {
		return (
			<>
				{createPortal(
					<VideoTrack
						ref={videoRef}
						className={styles.screenVideo}
						trackRef={trackRef}
						manageSubscription={false}
						data-flx="voice-scene.voice-scene-tv.tv.screen-video"
					/>,
					screenElement,
				)}
				<TvBar
					handle={handle}
					screen={screen}
					playing
					screenElement={screenElement}
					data-flx="voice-scene.voice-scene-tv.tv.tv-bar"
				>
					{picker}
					{linkBox}
					<TvIconButton
						label={FULL_SCREEN_DESCRIPTOR}
						icon={<CornersOutIcon weight="bold" />}
						onClick={() => VoiceSceneTvRequest.requestFullscreen(source)}
						data-flx="voice-scene.voice-scene-tv.tv.tv-icon-button.request-fullscreen"
					/>
					<TvIconButton
						label={CLOSE_DESCRIPTOR}
						icon={<XIcon weight="bold" />}
						onClick={onStop}
						data-flx="voice-scene.voice-scene-tv.tv.tv-icon-button.close"
					/>
				</TvBar>
			</>
		);
	}
	return (
		<>
			<VoiceSceneAnchor
				point={screen.center}
				className={styles.posterAnchor}
				data-flx="voice-scene.voice-scene-tv.tv.voice-scene-anchor--2"
			>
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: mouse convenience for the 3D camera, like the room's drag and wheel; Watch and the bar's buttons are the keyboard path. */}
				<div
					ref={posterRef}
					className={styles.poster}
					role="group"
					aria-label={posterLabel}
					data-tv-poster=""
					onClick={(event) => focusOnTv(handle, event.target)}
					onDoubleClick={(event) => focusOnTv(handle, event.target)}
					data-flx="voice-scene.voice-scene-tv.tv.poster"
				>
					<div className={styles.posterWho} data-flx="voice-scene.voice-scene-tv.tv.poster-who">
						<Avatar
							user={entry.user}
							size={POSTER_AVATAR_SIZE}
							guildId={channel.guildId ?? null}
							data-flx="voice-scene.voice-scene-tv.tv.avatar"
						/>
						<span className={styles.posterName} data-flx="voice-scene.voice-scene-tv.tv.poster-name">
							{posterLabel}
						</span>
						<span
							className={styles.posterLive}
							aria-hidden="true"
							data-flx="voice-scene.voice-scene-tv.tv.poster-live"
						/>
					</div>
					{canWatch ? (
						<Button small compact onClick={onWatch} data-flx="voice-scene.voice-scene-tv.tv.button--2">
							{i18n._(WATCH_DESCRIPTOR)}
						</Button>
					) : null}
				</div>
			</VoiceSceneAnchor>
			{picker || linkBox ? (
				<TvBar handle={handle} screen={screen} data-flx="voice-scene.voice-scene-tv.tv.tv-bar--2">
					{picker}
					{linkBox}
				</TvBar>
			) : null}
		</>
	);
});

// Whether the TV shows big enough for YouTube's player, from the quad as drawn: no layout read, and a state change only
// when the answer changes, not on every frame the camera moves.
const tvPlayable = (handle: VoiceSceneHandle): boolean =>
	(handle.screenShortEdgePx ? handle.screenShortEdgePx() : handle.screenInView() ? MIN_PLAYER_EDGE_PX : 0) >=
	MIN_PLAYER_EDGE_PX;

function useTvPlayable(handle: VoiceSceneHandle): boolean {
	const [playable, setPlayable] = useState(() => tvPlayable(handle));
	useEffect(() => {
		let last = tvPlayable(handle);
		setPlayable(last);
		return handle.onViewChange(() => {
			const next = tvPlayable(handle);
			if (next === last) return;
			last = next;
			setPlayable(next);
		});
	}, [handle]);
	return playable;
}

// A video on the TV, for everyone in the room on the shared time: someone's video link, or the video of a bot's stage.
// It plays on its own (the one iframe embed of the app: a playing chat embed gives way, as one does to another) until
// this viewer hides it (the opener, who changes it for everyone with the buttons in children, cannot). While hidden or
// given way, the thumbnail stays on the TV.
const LinkTv = observer(function LinkTv({
	handle,
	screen,
	watch,
	label,
	opener,
	onScreenPlaying,
	children,
}: {
	handle: VoiceSceneHandle;
	screen: Screen;
	watch: VoiceWatch;
	label: string;
	opener: boolean;
	onScreenPlaying?: (playing: boolean) => void;
	children: React.ReactNode;
}) {
	const {i18n} = useLingui();
	const embedId = useId();
	useEffect(() => {
		ActiveIframeEmbed.claim(embedId);
		return () => ActiveIframeEmbed.release(embedId);
	}, [embedId]);
	const watching = ActiveIframeEmbed.isActive(embedId);
	useScreenPlaying(handle, watching, onScreenPlaying);
	const playable = useTvPlayable(handle);
	// Focused on the TV, a click cannot bring it any closer.
	const clickToWatch = useCameraMode(handle) !== 'focus';
	const [screenElement] = useState(() => {
		const element = document.createElement('div');
		element.className = styles.screen;
		return element;
	});
	useEffect(() => {
		const focus = (event: Event) => focusOnTv(handle, event.target);
		screenElement.addEventListener('click', focus);
		screenElement.addEventListener('dblclick', focus);
		handle.attachScreenElement(screenElement);
		return () => {
			screenElement.removeEventListener('click', focus);
			screenElement.removeEventListener('dblclick', focus);
			handle.attachScreenElement(null);
		};
	}, [handle, screenElement]);
	return (
		<>
			{createPortal(
				watching ? (
					<VoiceSceneTvVideo
						key={watch.videoId}
						watch={watch}
						playable={playable}
						clickToWatch={clickToWatch}
						label={label}
						data-flx="voice-scene.voice-scene-tv.link-tv.voice-scene-tv-video"
					/>
				) : (
					<div className={tvStyles.video} data-flx="voice-scene.voice-scene-tv.link-tv.div">
						<img
							className={tvStyles.videoThumbnail}
							src={youTubeThumbnailUrl(watch.videoId)}
							referrerPolicy="no-referrer"
							alt={label}
							data-flx="voice-scene.voice-scene-tv.link-tv.img"
						/>
					</div>
				),
				screenElement,
			)}
			<TvBar
				handle={handle}
				screen={screen}
				playing={watching}
				screenElement={screenElement}
				data-flx="voice-scene.voice-scene-tv.link-tv.tv-bar"
			>
				{children}
				{watching ? (
					<VoiceSceneTvCaptions data-flx="voice-scene.voice-scene-tv.link-tv.voice-scene-tv-captions" />
				) : null}
				{watching ? <VoiceSceneTvVolume data-flx="voice-scene.voice-scene-tv.link-tv.voice-scene-tv-volume" /> : null}
				{!watching ? (
					<Button
						small
						compact
						onClick={() => ActiveIframeEmbed.claim(embedId)}
						data-flx="voice-scene.voice-scene-tv.link-tv.button.claim"
					>
						{i18n._(WATCH_DESCRIPTOR)}
					</Button>
				) : opener ? null : (
					<TvIconButton
						label={HIDE_DESCRIPTOR}
						icon={<EyeSlashIcon weight="bold" />}
						onClick={() => ActiveIframeEmbed.release(embedId)}
						data-flx="voice-scene.voice-scene-tv.link-tv.tv-icon-button.release"
					/>
				)}
			</TvBar>
		</>
	);
});
